import React, { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { FiPaperclip, FiSend, FiX } from 'react-icons/fi'
import './ClientTicketChat.css'

import { apiRequest, errorMessage, formatDate, formatTime } from './supportApi'
import { Awaiting, ChatMessage } from './clientTicketTypes'

const POLL_MS = 8000

export interface ChatMeta {
    status: string
    awaiting_customer: Awaiting
    can_chat: boolean
}

interface Props {
    ticketId: number
    perspective: 'client' | 'staff'
    /** Called after each fetch, so the parent can refresh when status changes */
    onMeta?: (meta: ChatMeta) => void
    /** Change this number to make the chat refetch right away (e.g. after an action) */
    refreshKey?: number
    placeholder?: string
}

function dayKey(iso: string) {
    return new Date(iso).toDateString()
}

export default function ClientTicketChat({ ticketId, perspective, onMeta, refreshKey = 0, placeholder }: Props) {
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [loading, setLoading] = useState(true)
    const [canChat, setCanChat] = useState(false)
    const [body, setBody] = useState('')
    const [files, setFiles] = useState<File[]>([])
    const [sending, setSending] = useState(false)

    const lastIdRef = useRef(0)
    const listRef = useRef<HTMLDivElement>(null)
    const fileRef = useRef<HTMLInputElement>(null)
    const onMetaRef = useRef(onMeta)
    onMetaRef.current = onMeta

    const merge = (incoming: ChatMessage[]) => {
        if (!incoming.length) return
        setMessages(prev => {
            const seen = new Set(prev.map(m => m.id))
            const next = [...prev, ...incoming.filter(m => !seen.has(m.id))]
            lastIdRef.current = Math.max(lastIdRef.current, ...next.map(m => m.id))
            return next
        })
    }

    const load = useCallback(async (initial = false) => {
        const after = initial ? 0 : lastIdRef.current
        const { ok, data } = await apiRequest(
            `/portal/api/client-tickets/${ticketId}/messages/${after ? `?after=${after}` : ''}`)
        if (ok) {
            if (initial) {
                lastIdRef.current = 0
                setMessages([])
            }
            merge(data.messages)
            setCanChat(data.can_chat)
            onMetaRef.current?.({ status: data.status, awaiting_customer: data.awaiting_customer, can_chat: data.can_chat })
        }
        if (initial) setLoading(false)
    }, [ticketId])

    // Initial load + polling (paused while the tab is hidden)
    useEffect(() => {
        setLoading(true)
        load(true)
        const timer = window.setInterval(() => { if (!document.hidden) load() }, POLL_MS)
        return () => window.clearInterval(timer)
    }, [load])

    useEffect(() => { if (refreshKey) load() }, [refreshKey, load])

    // Keep the newest message in view
    useEffect(() => {
        const el = listRef.current
        if (el) el.scrollTop = el.scrollHeight
    }, [messages.length, loading])

    const send = async () => {
        const text = body.trim()
        if (!text && !files.length) return
        setSending(true)
        const form = new FormData()
        form.append('body', text)
        files.forEach(f => form.append('attachments', f))

        const { ok, data } = await apiRequest(`/portal/api/client-tickets/${ticketId}/messages/`, { method: 'POST', form })
        setSending(false)
        if (!ok) {
            toast.error(errorMessage(data, 'Message not sent. Try again.'))
            return
        }
        merge([data])
        setBody('')
        setFiles([])
        load()   // picks up any system message (e.g. "work has resumed") and new status
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (!sending) send()
        }
    }

    const roleTag = (m: ChatMessage) => {
        if (perspective === 'client') return m.author_role === 'fnn' ? 'FNN Support' : null
        return m.author_role === 'client' ? 'Customer' : null
    }

    return (
        <div className="tchat">
            <div className="tchat-list" ref={listRef} aria-live="polite">
                {loading && <div className="tchat-empty">Loading conversation…</div>}

                {!loading && messages.filter(m => !m.is_system).length === 0 && (
                    <div className="tchat-empty">
                        {perspective === 'client'
                            ? 'No messages yet. Add details or questions here and our team will reply.'
                            : 'No messages yet. Messages here are visible to the customer.'}
                    </div>
                )}

                {messages.map((m, i) => {
                    const newDay = i === 0 || dayKey(messages[i - 1].created_at) !== dayKey(m.created_at)
                    return (
                        <React.Fragment key={m.id}>
                            {newDay && <div className="tchat-day">{formatDate(m.created_at)}</div>}

                            {m.is_system ? (
                                <div className="tchat-system">
                                    <span>{m.body}</span>
                                    <time>{formatTime(m.created_at)}</time>
                                </div>
                            ) : (
                                <div className={`tchat-msg ${m.is_mine ? 'is-mine' : ''} ${m.author_role === 'client' ? 'is-client' : 'is-fnn'}`}>
                                    {!m.is_mine && (
                                        <div className="tchat-author">
                                            {m.author_name}
                                            {roleTag(m) && <span className="tchat-role">{roleTag(m)}</span>}
                                        </div>
                                    )}
                                    <div className="tchat-bubble">
                                        {m.body && <p>{m.body}</p>}
                                        {m.attachments.length > 0 && (
                                            <div className="tchat-files">
                                                {m.attachments.map(a => (
                                                    <a key={a.attachment_id} href={a.file_url} target="_blank" rel="noopener noreferrer">
                                                        <FiPaperclip size={12} /> {a.file_name}
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <time className="tchat-time">{formatTime(m.created_at)}</time>
                                </div>
                            )}
                        </React.Fragment>
                    )
                })}
            </div>

            {canChat ? (
                <div className="tchat-composer">
                    {files.length > 0 && (
                        <div className="tchat-pending">
                            {files.map((f, i) => (
                                <span key={i} className="tchat-pending-file">
                                    {f.name}
                                    <button type="button" aria-label={`Remove ${f.name}`}
                                        onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>
                                        <FiX size={11} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="tchat-input-row">
                        <button type="button" className="tchat-icon-btn" aria-label="Attach files"
                            onClick={() => fileRef.current?.click()}>
                            <FiPaperclip size={16} />
                        </button>
                        <textarea
                            rows={1}
                            value={body}
                            onChange={e => setBody(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder={placeholder ?? 'Write a message…'}
                            aria-label="Message"
                        />
                        <button type="button" className="tchat-send" onClick={send}
                            disabled={sending || (!body.trim() && !files.length)}>
                            <FiSend size={14} />
                            <span>{sending ? 'Sending…' : 'Send'}</span>
                        </button>
                    </div>
                    <input ref={fileRef} type="file" multiple hidden
                        onChange={e => { if (e.target.files) setFiles(prev => [...prev, ...Array.from(e.target.files!)]); e.target.value = '' }} />
                    <div className="tchat-hint">Press Enter to send, Shift + Enter for a new line</div>
                </div>
            ) : (
                !loading && (
                    <div className="tchat-readonly">
                        You can read this conversation. The receiver, team leader and assignee can reply.
                    </div>
                )
            )}
        </div>
    )
}